import { getUnsoldFirstSortGroup, INVENTORY_ROW_CLICK_IGNORED_COLUMNS } from "@src/constants/constants";
import { unsoldItemIds } from "@src/components/bulkTag/bulkTag.utils";
import helperStyles from "@src/components/helpers.module.scss";
import { Loader } from "@src/components/loader/Loader";
import { SectionContainer } from "@src/components/sectionContainer/SectionContainer";
import { convertSeatNumbersToSeats } from "@src/components/utils/columnUtils";
import { IRowDataValueChangeProps, VGrid, VGridRef } from "@src/components/vGrid/VGrid";
import { eventActions, mapActions, pricingActions, systemActions } from "@src/store";
import { useAppDispatch, useAppSelector } from "@src/store/hooks";
import { EventInventoryRecord } from "@src/store/pricing/queryEventInventoryAllSlice";
import { matchesFilterTokens, matchesSectionFilterTokens, parseFilterTokens } from "@src/utils/sectionFilter.utils";
import { IListingToUpdate } from "@src/store/pricing/updateListingPrices";
import { IInvoiceWithCost } from "@src/store/system/updateCostPerTicket";
import _ from "lodash";
import { getInventoryGroupKey } from "@src/components/utils/inventoryUtils";
import { collapseMarketplaceSync } from "@src/components/utils/syncStatus";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { InventoryHeadline } from "./components/InventoryHeadline";
import { readAllPendingChanges, removePendingChange, writePendingChange } from "./inventoryPendingChanges";
import InventoryContextMenu from "@src/components/contextMenu/inventory/InventoryContextMenu";
import ContextMenu, { ContextMenuHandle } from "@src/components/contextMenu/ContextMenu";
import { IModalHandle, Modal } from "@src/components/modal/Modal";
import { CommonModal } from "@src/components/modal/CommonModal";
import HelpAndSupport from "@src/components/helpAndSupport/HelpAndSupport";
import ContactUser from "@src/components/contactUser/ContactUser";
import InvoiceForm from "@src/components/invoiceForm/InvoiceForm";
import InternalInvoiceForm from "@src/components/internalInvoiceForm/InternalInvoiceForm";
import EventContextHeader from "@src/components/eventContextHeader/EventContextHeader";
import { OptionType } from "@src/components/utils/reactSelectUtils";
import { MixpanelCategory, mixpanelTrack } from "@src/components/utils/mixPanelUtils";
import { Button, ButtonVariant } from "@src/components/button/Button";
import { toast } from "sonner";
import { ViewHistory } from "@src/components/viewHistory/ViewHistory";
import { formatDate, DateFormatsEnum } from "@src/components/utils/dateUtils";
import { SchedulePrice } from "../manage-inventory/manage-listing/AdvancedTab/components/SchedulePrice/SchedulePrice";
import { FlashSale } from "../manage-inventory/manage-listing/AdvancedTab/components/FlashSale/FlashSale";
import { PurchaseInformation } from "../manage-inventory/manage-listing/AdvancedTab/components/PurchaseInformation/PurchaseInformation";
import { SplitListing } from "../manage-inventory/manage-listing/AdvancedTab/components/SplitListing/SplitListing";
import { ExternalNotes } from "@src/components/extenalNotes/ExternalNotes";
import { TagComponent } from "@src/components/tagComponent/TagComponent";
import { ShareUnshare } from "../manage-inventory/manage-listing/ShareUnshare";
import InternalNotesModal from "@src/components/internalNotes/internalNotesModal/InternalNotesModal";
import { DuplicateListing } from "@src/components/duplicateListing/DuplicateListing";
import { ChangeSeats } from "@src/components/changeSeats/ChangeSeats";
import { useAutopricerListingSwitch } from "../manage-inventory/useAutopricerListingSwitch";
import {
	INVENTORY_TOGGLE_KEYS,
	readStoredBoolean,
	readStoredBooleanOrNull,
	writeStoredBoolean,
} from "@src/utils/gridPreferences.utils";

export const getGroupedListingCost = (items: Array<EventInventoryRecord>): number =>
	items.reduce((acc, curr) => acc + curr.Item_Cost + (curr.Buyers_Overs ?? 0), 0);

export let externalAutoPricerRowClick: (row?: EventInventoryRecord) => void = () => void 0;

const getInventoryRowNodeId = (data: EventInventoryRecord) =>
	`${data.Invoice_ID ? "Sold" : "NotSold"}-${data.Listing_ID}-${data.Item_ID}`;

export const Inventory: React.FC = () => {
	const dispatch = useAppDispatch();
	const autoApplyFilters = useAppSelector((state) => state.map.autoApplyFilters);

	const helpAndSupportRef = useRef<IModalHandle>(null);
	const contactUserRef = useRef<IModalHandle>(null);
	const createInvoiceContextRef = useRef<IModalHandle>(null);
	const internalInvoiceRef = useRef<IModalHandle>(null);
	const schedulePriceModalRef = useRef<IModalHandle>(null);
	const flashSaleModalRef = useRef<IModalHandle>(null);
	const purchaseInformationModalRef = useRef<IModalHandle>(null);
	const tagModalRef = useRef<IModalHandle>(null);
	const splitListingModalRef = useRef<IModalHandle>(null);
	const externalNotesModalRef = useRef<IModalHandle>(null);
	const shareUnshareModalRef = useRef<IModalHandle>(null);
	const internalNotesModalRef = useRef<IModalHandle>(null);
	const relistModalRef = useRef<IModalHandle>(null);
	const eDeliveryModalRef = useRef<IModalHandle>(null);
	const duplicateModalRef = useRef<IModalHandle>(null);
	const viewHistoryModalRef = useRef<IModalHandle>(null);
	const changeSeatsModalRef = useRef<IModalHandle>(null);

	const vGridRef = useRef<VGridRef>(null);

	const [searchParams] = useSearchParams();
	const [pageState, setPageState] = useState({
		changesMap: new Map<string, IRowDataValueChangeProps<EventInventoryRecord>>(),
		changesMapVersion: 0,
	});

	const [selectedRow, setSelectedRow] = useState<EventInventoryRecord | null>(null);
	const contextRef = useRef<ContextMenuHandle>(null);
	const closeContextMenu = () => contextRef.current?.updateContextMenu({ isOpen: false });

	const handleScroll = () => {
		const contextData = contextRef.current?.contextData;
		if (!contextData?.isOpen) return;

		closeContextMenu();
	};

	const showSoldEvents = readStoredBooleanOrNull(INVENTORY_TOGGLE_KEYS.showSold) ?? searchParams.get("showSold") === "true";
	const showOnHoldEvents =
		readStoredBooleanOrNull(INVENTORY_TOGGLE_KEYS.showOnHold) ?? searchParams.get("showOnHold") !== "false";
	const [showSold, setShowSold] = useState(showSoldEvents);
	const [showOnHold, setShowOnHold] = useState(showOnHoldEvents);
	const [groupInventory, setGroupInventory] = useState(() => readStoredBoolean(INVENTORY_TOGGLE_KEYS.groupInventory, true));

	const handleChecked = () => {
		setShowSold((prev) => {
			writeStoredBoolean(INVENTORY_TOGGLE_KEYS.showSold, !prev);
			return !prev;
		});
		mixpanelTrack(MixpanelCategory.SHOW_SOLD_INVENTORY);
	};

	const handleOnHoldChecked = () => {
		setShowOnHold((prev) => {
			writeStoredBoolean(INVENTORY_TOGGLE_KEYS.showOnHold, !prev);
			return !prev;
		});
		mixpanelTrack(MixpanelCategory.SHOW_ON_HOLD_INVENTORY);
	};

	const handleGroupInventoryChecked = () => {
		setGroupInventory((prev) => {
			writeStoredBoolean(INVENTORY_TOGGLE_KEYS.groupInventory, !prev);
			return !prev;
		});
		vGridRef.current?.clearSelection();
		dispatch(eventActions.setInventory({ inventoryItemIds: [] }));
	};

	useEffect(() => {
		setShowSold(showSoldEvents);
		writeStoredBoolean(INVENTORY_TOGGLE_KEYS.showSold, showSoldEvents);
	}, [showSoldEvents]);

	useEffect(() => {
		setShowOnHold(showOnHoldEvents);
		writeStoredBoolean(INVENTORY_TOGGLE_KEYS.showOnHold, showOnHoldEvents);
	}, [showOnHoldEvents]);

	const eventInventoryAll = useAppSelector((state) => state.pricing.eventInventoryAll);
	const eventItem = useAppSelector((state) => state.pricing.selection.event);
	const inventoryItemIds = useAppSelector((state) => state.pricing.selection.inventoryItemIds);
	const inventoryItems = _.filter(eventInventoryAll.data, (x) => inventoryItemIds.includes(x.Item_ID));

	const previousSelectionCount = useRef(inventoryItemIds.length);

	useEffect(() => {
		const hadSelection = previousSelectionCount.current;
		previousSelectionCount.current = inventoryItemIds.length;
		if (inventoryItemIds.length || !hadSelection) return;
		vGridRef.current?.clearSelection();
	}, [inventoryItemIds.length]);
	const companyName = useAppSelector((state) => state.settings.companyName);
	const columnsInventory = useAppSelector((state) => state.pricing.settings.columns.Inventory);
	const columnsUngroupedInventory = useAppSelector((state) => state.pricing.settings.columns.UngroupedInventory);
	const isSaving = useAppSelector((state) => state.pricing.updateListingPrices.loading);
	const poId = searchParams.get("poId")?.toLocaleLowerCase() ?? "";
	const inventoryTags = searchParams.get("inventoryTags");
	const showUntaggedInventory = searchParams.get("showUntaggedInventory") === "true";
	const listingId = searchParams.get("listingId")?.toLocaleLowerCase() ?? "";
	const sectionFilter = searchParams.get("section") ?? "";
	const rowFilter = searchParams.get("row") ?? "";
	const sectionFilterTokens = useMemo(() => parseFilterTokens(sectionFilter), [sectionFilter]);
	const rowFilterTokens = useMemo(() => parseFilterTokens(rowFilter), [rowFilter]);
	const poStartDate = new Date(searchParams.get("poStartDate") ?? "");
	const poEndDate = new Date(searchParams.get("poEndDate") ?? "");
	// End date is inclusive — bump to next-day midnight so a same-day timestamped
	// PO_Date still matches.
	if (!isNaN(poEndDate.getTime())) poEndDate.setDate(poEndDate.getDate() + 1);

	const gridRowsRef = useRef<Array<EventInventoryRecord>>([]);
	const { interceptSelection, selectRowProgrammatically } = useAutopricerListingSwitch({
		gridRef: vGridRef,
		getRows: () => gridRowsRef.current,
		getRowNodeId: getInventoryRowNodeId,
	});

	const handleSelectable = useCallback(
		(rowsData: Array<EventInventoryRecord>) => {
			if (groupInventory && interceptSelection(rowsData)) return;

			let itemIdsToSelect: Array<number>;
			if (groupInventory) {
				itemIdsToSelect = rowsData.map((x) => x.Item_ID);
			} else {
				// Ungrouped: dedupe selection by listing and map each to its
				// "first-child" Item_ID so downstream bulk-action selectors
				// (which group raw data by Listing_ID and filter by value[0].Item_ID)
				// still find every selected listing.
				const sortedRaw = _.sortBy(eventInventoryAll.data, "Listing_ID");
				const groupedRaw = _.groupBy(sortedRaw, getInventoryGroupKey);
				const selectedKeys = new Set(rowsData.map(getInventoryGroupKey));
				itemIdsToSelect = Object.entries(groupedRaw)
					.filter(([key]) => selectedKeys.has(key))
					.map(([, items]) => items[0].Item_ID);
			}
			dispatch(
				eventActions.setInventory({
					inventoryItemIds: itemIdsToSelect,
				}),
			);

			if (autoApplyFilters) {
				dispatch(
					mapActions.applySelectedListings({
						sections: rowsData.map((x) => x.Section ?? ""),
					}),
				);
			}

			mixpanelTrack(MixpanelCategory.LISTING_CHECKBOX);
		},
		[dispatch, groupInventory, eventInventoryAll.data, autoApplyFilters, interceptSelection],
	);

	externalAutoPricerRowClick = useCallback(
		(row?: EventInventoryRecord) => {
			if (!row) return;
			selectRowProgrammatically(row);
		},
		[selectRowProgrammatically],
	);

	const eventInventory = eventInventoryAll.data;

	const sortedData = _.sortBy(eventInventory, "Listing_ID");
	const groupedData = _.groupBy(sortedData, getInventoryGroupKey);

	const groupedRowsData = _.map(groupedData, (value) => {
		const seatNumbers = value.map((x) => x.Seat_Number);
		const allItemIds = value.map((x) => x.Item_ID);
		const SH_eDelivery = value.some((x) => x.SH_eDelivery);
		const BarcodesAttachedNumber = value.reduce((acc, item) => {
			return acc + (item.Barcode_Attached === true ? 1 : 0);
		}, 0);

		const totalPDFsAttached = value.reduce((acc, item) => {
			return acc + (item.PDF_Attached === true ? 1 : 0);
		}, 0);

		return {
			...value[0],
			Seats: convertSeatNumbersToSeats(seatNumbers),
			Child_Item_IDs: allItemIds,
			Quantity: value.length,
			SH_eDelivery,
			BarcodesAttachedNumber,
			totalPDFsAttached,
			PDF_Attached: value.some((x) => x.PDF_Attached),
			Item_Cost: getGroupedListingCost(value),
			...collapseMarketplaceSync(value),
		};
	});

	gridRowsRef.current = groupedRowsData;

	const ungroupedRowsData = sortedData.map((item) => ({
		...item,
		Seats: String(item.Seat_Number ?? ""),
		Child_Item_IDs: [item.Item_ID],
		Quantity: 1,
		BarcodesAttachedNumber: item.Barcode_Attached ? 1 : 0,
		totalPDFsAttached: item.PDF_Attached ? 1 : 0,
	}));

	const rowsData = (groupInventory ? groupedRowsData : ungroupedRowsData).filter((x) => {
		const isOnHold = x.On_Hold || x.Consignor_Hold;
		if (!(showSold || !x.Invoice_ID)) return false;
		if (!(showOnHold || !isOnHold)) return false;

		if (sectionFilter && !matchesSectionFilterTokens(sectionFilterTokens, x.Section ?? "", x.Row ?? "")) return false;
		if (rowFilter && !matchesFilterTokens(rowFilterTokens, x.Row ?? "")) return false;

		const poDate = new Date(x.PO_Date ?? "");
		if (!isNaN(poStartDate.getTime()) && (isNaN(poDate.getTime()) || poDate < poStartDate)) return false;
		if (!isNaN(poEndDate.getTime()) && (isNaN(poDate.getTime()) || poDate >= poEndDate)) return false;

		return true;
	});

	const rehydratedChangesMap = useMemo(() => {
		const map = new Map<string, IRowDataValueChangeProps<EventInventoryRecord>>();
		const pending = readAllPendingChanges();
		if (pending.size === 0) return map;

		const activeColumns = groupInventory ? columnsInventory : columnsUngroupedInventory;
		const editableColumns = activeColumns.filter((c) => c.accessor && c.isEditable);
		if (!editableColumns.length) return map;

		const sorted = _.sortBy(eventInventoryAll.data, "Listing_ID");
		const rows = groupInventory
			? _.map(
					_.groupBy(sorted, getInventoryGroupKey),
					(value) => ({
						...value[0],
						Quantity: value.length,
						Item_Cost: getGroupedListingCost(value),
					}),
				)
			: sorted;

		rows.forEach((row) => {
			const rowId = `${row.Invoice_ID ? "Sold" : "NotSold"}-${row.Listing_ID}-${row.Item_ID}`;
			editableColumns.forEach((col) => {
				if (!col.accessor) return;
				const changeMapId = `row:${rowId}-col:${col.id}`;
				const stored = pending.get(changeMapId);
				if (stored == null) return;
				const originalValue = col.valueGetter
					? col.valueGetter({ rowData: row as EventInventoryRecord, column: col })
					: (row as EventInventoryRecord)[col.accessor];
				map.set(changeMapId, {
					value: stored,
					originalValue,
					columnId: col.id,
					accesor: col.accessor,
					rowId,
					rowData: row as EventInventoryRecord,
				});
			});
		});
		return map;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [eventInventoryAll.data, groupInventory, columnsInventory, columnsUngroupedInventory]);

	const tableContent = useMemo(() => {
		if (eventInventoryAll.loading) return <Loader />;
		if (!eventInventory.length) return <div className={helperStyles.fullWidthHeightCenter}>No data</div>;

		let rowsDataInner = rowsData;

		if (inventoryTags && !showUntaggedInventory) {
			rowsDataInner = rowsDataInner.filter((x) => x.Tags?.includes(inventoryTags));
		}

		if (listingId) {
			rowsDataInner = rowsDataInner.filter((x) => x.Listing_ID == +listingId);
		}

		if (showUntaggedInventory) {
			rowsDataInner = rowsDataInner.filter((x) => x.Tags === "" || !x.Tags);
		}

		if (poId) {
			rowsDataInner = rowsDataInner.filter((x) => x.PO_ID == +poId);
		}

		const activeColumns = groupInventory ? columnsInventory : columnsUngroupedInventory;
		return rowsData.length ? (
			<VGrid<EventInventoryRecord>
				tableId={"inventory"}
				getSortGroup={getUnsoldFirstSortGroup}
				ref={vGridRef}
				rowsData={rowsDataInner}
				columns={activeColumns}
				wrapperStyle={{ padding: "0px 8px 8px 8px" }}
				rowSelection={groupInventory ? "multiple" : "none"}
				onSelectionChange={groupInventory ? handleSelectable : undefined}
				rowClickSelectionIgnoredColumns={INVENTORY_ROW_CLICK_IGNORED_COLUMNS}
				getCheckboxInputStyle={(rowData) => {
					if (!groupInventory) return { display: "none" };
					return rowData.Invoice_ID ? { display: "none" } : {};
				}}
				getCheckboxIsDisabled={(rowData) => {
					if (!groupInventory) return true;
					return !!rowData.Invoice_ID;
				}}
				getRowNodeId={getInventoryRowNodeId}
				getRowClassName={({ rowData }) => {
					if (rowData.Invoice_ID) return helperStyles.rowWithSold;
					if (rowData.On_Hold || rowData.Consignor_Hold) return helperStyles.rowWithOnHold;
					return "";
				}}
				changesMapVersion={pageState.changesMapVersion}
				enableEnterAdvance
				initialChangesMap={rehydratedChangesMap}
				onRowDataValueChange={(params) => {
					const changeMapId = `row:${params.rowId}-col:${params.columnId}`;
					if (params.value == params.originalValue) {
						removePendingChange(changeMapId);
					} else {
						writePendingChange(changeMapId, params.value);
					}
				}}
				onChangeMapChange={(changesMap) => {
					dispatch(eventActions.setInventoryHasChanges({ inventoryHasChanges: changesMap.size > 0 }));
					setPageState((x) => ({ ...x, changesMap }));
				}}
				isSaving={isSaving}
				getContextMenuInfo={
					groupInventory
						? ({ rowData, x, y }) => {
								setSelectedRow(rowData);
								mixpanelTrack(MixpanelCategory.CONTEXT_MENU_INVENTORY);
								contextRef.current?.updateContextMenu({ x: x ?? 0, y: y ?? 0, isOpen: true });
							}
						: undefined
				}
			/>
		) : (
			<div className={helperStyles.fullWidthHeightCenter}>{eventInventoryAll.error ?? "No data"}</div>
		);
	}, [
		eventInventoryAll.loading,
		handleSelectable,
		columnsInventory,
		columnsUngroupedInventory,
		groupInventory,
		pageState.changesMapVersion,
		isSaving,
		dispatch,
		eventInventoryAll.error,
		eventInventory.length,
		poId,
		rowsData,
		inventoryTags,
		listingId,
		showUntaggedInventory,
		rehydratedChangesMap,
	]);

	const fetchInventory = useCallback(
		(skipCache = false) => {
			if (!eventItem?.Event_ID) {
				dispatch(
					pricingActions.updateInventoryAll({
						eventId: 0,
						data: [],
					}),
				);
				dispatch(
					eventActions.setInventory({
						inventoryItemIds: [],
						isEditListingEnabled: false,
					}),
				);
				return;
			}
			const res = dispatch(
				pricingActions.queryEventInventoryAll({
					EventId: eventItem.Event_ID,
					CompanyName: companyName,
					skipCache,
				}),
			);

			return res;
		},
		[companyName, dispatch, eventItem?.Event_ID],
	);

	useEffect(() => {
		const res = fetchInventory();
		return () => res?.abort();
	}, [eventItem?.Event_ID, fetchInventory]);

	const resetChangeMap = () => {
		pageState.changesMap.forEach((_value, changeMapId) => removePendingChange(changeMapId));
		dispatch(eventActions.setInventoryHasChanges({ inventoryHasChanges: false }));
		setPageState((x) => ({ ...x, changesMap: new Map(), changesMapVersion: x.changesMapVersion + 1 }));
	};

	const openHelpAndSupportModal = () => helpAndSupportRef.current?.openModal();
	const closeHelpAndSupportModal = () => helpAndSupportRef.current?.closeModal();

	const openContactUserModal = () => contactUserRef.current?.openModal();
	const closeContactUserModal = () => contactUserRef.current?.closeModal();

	const openCreateInvoiceContextModal = () => createInvoiceContextRef.current?.openModal();
	const closeCreateInvoiceContextModal = () => createInvoiceContextRef.current?.closeModal();

	const openInternalInvoiceModal = () => internalInvoiceRef.current?.openModal();
	const closeInternalInvoiceModal = () => internalInvoiceRef.current?.closeModal();

	const invoiceSeats = useMemo<Array<OptionType<number>>>(() => {
		const listingId = selectedRow?.Listing_ID;
		if (!listingId) return [];
		return eventInventoryAll.data
			.filter((x) => x.Listing_ID === listingId && !x.Invoice_ID)
			.map((x) => ({ value: x.Seat_Number, label: String(x.Seat_Number) }));
	}, [eventInventoryAll.data, selectedRow?.Listing_ID]);

	const invoiceSeatItems = useMemo<Array<EventInventoryRecord>>(() => {
		const listingId = selectedRow?.Listing_ID;
		if (!listingId) return [];
		return eventInventoryAll.data.filter((x) => x.Listing_ID === listingId && !x.Invoice_ID);
	}, [eventInventoryAll.data, selectedRow?.Listing_ID]);

	const contextListings = selectedRow ? [{ section: selectedRow.Section, row: selectedRow.Row, seats: selectedRow.Seats }] : [];

	const openSchedulePriceModal = () => schedulePriceModalRef.current?.openModal();
	const closeSchedulePriceModal = () => schedulePriceModalRef.current?.closeModal();

	const openFlashSaleModal = () => flashSaleModalRef.current?.openModal();
	const closeFlashSaleModal = () => flashSaleModalRef.current?.closeModal();

	const openPurchaseInformationModal = () => purchaseInformationModalRef.current?.openModal();
	const closePurchaseInformationModal = () => purchaseInformationModalRef.current?.closeModal();

	const openTagModal = () => tagModalRef.current?.openModal();
	const closeTagModal = () => tagModalRef.current?.closeModal();

	const openSplitListingModal = () => splitListingModalRef.current?.openModal();
	const closeSplitListingModal = () => splitListingModalRef.current?.closeModal();

	const openExternalNotesModal = () => externalNotesModalRef.current?.openModal();
	const closeExternalNotesModal = () => externalNotesModalRef.current?.closeModal();

	const openShareUnshareModal = () => shareUnshareModalRef.current?.openModal();
	const closeShareUnshareModal = () => shareUnshareModalRef.current?.closeModal();

	const openInternalNotesModal = () => internalNotesModalRef.current?.openModal();
	const closeInternalNotesModal = () => internalNotesModalRef.current?.closeModal();

	const openRelistModal = () => relistModalRef.current?.openModal();
	const closeRelistModal = () => relistModalRef.current?.closeModal();

	const relistLoading = useAppSelector((state) => state.pricing.relistListings.loading);

	const handleRelistConfirm = async () => {
		const listingId = selectedRow?.Listing_ID;
		if (!listingId) return;

		try {
			await dispatch(pricingActions.relistListings({ ListingIds: [listingId] })).unwrap();
			toast.success(`Listing ${listingId} re-listed successfully`);
			closeRelistModal();
			fetchInventory(true);
		} catch {
			toast.error("Failed to re-list listing");
		}
	};

	const openEDeliveryModal = () => eDeliveryModalRef.current?.openModal();
	const closeEDeliveryModal = () => eDeliveryModalRef.current?.closeModal();

	const eDeliveryLoading = useAppSelector((state) => state.pricing.editTicketStock.loading);
	const isEDeliveryOn = !!selectedRow?.SH_eDelivery;

	const handleEDeliveryConfirm = async () => {
		const listingId = selectedRow?.Listing_ID;
		if (!listingId) return;

		try {
			await dispatch(
				pricingActions.editTicketStock({
					ListingIds: [listingId],
					TicketStock: isEDeliveryOn ? "HARD" : "PDF",
				}),
			).unwrap();
			toast.success(`E-Delivery ${isEDeliveryOn ? "removed from" : "added to"} listing ${listingId}`);
			closeEDeliveryModal();
		} catch {
			toast.error(`Failed to ${isEDeliveryOn ? "remove" : "add"} E-Delivery`);
		}
	};

	const openDuplicateModal = () => duplicateModalRef.current?.openModal();
	const closeDuplicateModal = () => duplicateModalRef.current?.closeModal();

	const openChangeSeatsModal = () => changeSeatsModalRef.current?.openModal();
	const closeChangeSeatsModal = () => changeSeatsModalRef.current?.closeModal();

	const openViewHistoryModal = () => viewHistoryModalRef.current?.openModal();
	const closeViewHistoryModal = () => viewHistoryModalRef.current?.closeModal();

	return (
		<SectionContainer
			onWheel={handleScroll}
			headline={
				<InventoryHeadline
					showSold={showSold}
					showOnHold={showOnHold}
					groupInventory={groupInventory}
					handleChecked={handleChecked}
					handleOnHoldChecked={handleOnHoldChecked}
					handleGroupInventoryChecked={handleGroupInventoryChecked}
					fetchInventory={fetchInventory}
					rowsData={eventInventoryAll.loading ? [] : rowsData}
					data={inventoryItems}
					changesMap={pageState.changesMap}
					onApply={async () => {
						const listingsToUpdate = new Array<IListingToUpdate>();
						const costToUpdate = new Array<IInvoiceWithCost>();
						const unsoldCostToUpdate = new Array<{ cost: number; listing_id: number }>();

						pageState.changesMap.forEach((value) => {
							if (value.columnId == "Cost") {
								const invoiceID = value.rowData.Invoice_ID;
								if (invoiceID) {
									costToUpdate.push({
										Invoice_ID: invoiceID,
										Item_Cost: Number(value.value),
									});
								} else {
									unsoldCostToUpdate.push({
										listing_id: value.rowData.Listing_ID,
										cost: Number(value.value),
									});
								}
							} else {
								const listingId = value.rowData.Listing_ID;

								listingsToUpdate.push({
									listing_id: listingId,
									price: Number(value.value),
									face: null,
								});
							}
						});

						let shouldRefresh = false;

						if (costToUpdate.length) {
							await dispatch(
								systemActions.updateCostPerTicket({
									UpdatedCosts: costToUpdate,
								}),
							);
							shouldRefresh = true;
						}

						if (unsoldCostToUpdate.length) {
							await dispatch(
								pricingActions.updateListingCosts({
									CostUpdates: unsoldCostToUpdate,
									CostLocked: false,
								}),
							);
							shouldRefresh = true;
						}

						if (listingsToUpdate.length) {
							await dispatch(pricingActions.updateListingPrices(listingsToUpdate));
							mixpanelTrack(MixpanelCategory.PRICE_UPDATE);
							shouldRefresh = false;
						}

						if (shouldRefresh) {
							fetchInventory(true);
						}

						resetChangeMap();
					}}
					onDiscard={async () => {
						resetChangeMap();
					}}
				/>
			}>
			<div data-listing-selection-area={groupInventory ? "" : undefined} className={helperStyles.displayContents}>
				{tableContent}
			</div>
			<Modal ref={helpAndSupportRef}>
				<CommonModal
					style={{ padding: 0 }}
					closeModal={closeHelpAndSupportModal}
					content={<HelpAndSupport selectedRow={selectedRow} closeContextMenu={closeHelpAndSupportModal} />}
					title={"Help & Support"}
				/>
			</Modal>
			<Modal ref={contactUserRef}>
				<CommonModal
					style={{ padding: 0 }}
					closeModal={closeContactUserModal}
					content={<ContactUser selectedRow={selectedRow} closeContextMenu={closeContactUserModal} />}
					title={"Contact User"}
				/>
			</Modal>
			<Modal ref={createInvoiceContextRef}>
				<CommonModal
					width="auto"
					padding="0px"
					closeModal={closeCreateInvoiceContextModal}
					title={"Invoice Listing"}
					content={
						<InvoiceForm
							key={selectedRow?.Listing_ID ?? "create-invoice"}
							item={selectedRow ?? undefined}
							event={eventItem}
							availableSeats={invoiceSeats}
							onClose={closeCreateInvoiceContextModal}
						/>
					}
				/>
			</Modal>

			<Modal ref={internalInvoiceRef}>
				<CommonModal
					width="auto"
					padding="0px"
					closeModal={closeInternalInvoiceModal}
					title={"Internal Invoice"}
					content={
						<InternalInvoiceForm
							key={selectedRow?.Listing_ID ?? "internal-invoice"}
							item={selectedRow ?? undefined}
							event={eventItem}
							seatItems={invoiceSeatItems}
							onClose={closeInternalInvoiceModal}
							onSuccess={() => fetchInventory(true)}
						/>
					}
				/>
			</Modal>

			<Modal style={{ overflow: "visible", width: 550 }} ref={shareUnshareModalRef}>
				<CommonModal
					style={{ padding: 20, overflow: "visible" }}
					closeModal={closeShareUnshareModal}
					content={
						<>
							<EventContextHeader listings={contextListings} />
							<ShareUnshare
								key={selectedRow?.Listing_ID}
								isSold={!!selectedRow?.Invoice_ID}
								listingId={selectedRow?.Listing_ID}
								onSuccess={closeShareUnshareModal}
							/>
						</>
					}
					title={"Share/Unshare"}
				/>
			</Modal>

			<Modal ref={externalNotesModalRef}>
				<CommonModal
					style={{ padding: "0px 20px 10px" }}
					closeModal={closeExternalNotesModal}
					content={
						<>
							<EventContextHeader listings={contextListings} />
							<ExternalNotes
								wrapperStyle={{ paddingTop: "20px" }}
								itemID={selectedRow?.Item_ID ?? 0}
								listingId={selectedRow?.Listing_ID ?? 0}
								notesText={selectedRow?.External_Notes ?? ""}
							/>
						</>
					}
					title={"External Notes"}
				/>
			</Modal>
			<Modal ref={internalNotesModalRef}>
				<InternalNotesModal
					key={selectedRow?.Listing_ID ?? selectedRow?.Invoice_ID ?? "internal-notes"}
					listingId={selectedRow?.Listing_ID?.toString()}
					invoiceId={selectedRow?.Invoice_ID?.toString()}
					onClose={closeInternalNotesModal}
					onSaved={() => fetchInventory(true)}
					eventHeaderListings={contextListings}
				/>
			</Modal>
			<Modal ref={schedulePriceModalRef}>
				<CommonModal
					style={{ padding: 20 }}
					closeModal={closeSchedulePriceModal}
					content={
						<>
							<EventContextHeader listings={contextListings} />
							<SchedulePrice inventoryItem={selectedRow ?? undefined} onSuccess={closeSchedulePriceModal} />
						</>
					}
					title={"Schedule Price"}
				/>
			</Modal>
			<Modal ref={flashSaleModalRef}>
				<CommonModal
					style={{ padding: 20 }}
					closeModal={closeFlashSaleModal}
					content={
						<>
							<EventContextHeader listings={contextListings} />
							<FlashSale inventoryItem={selectedRow ?? undefined} onSuccess={closeFlashSaleModal} />
						</>
					}
					title={"Flash Sale"}
				/>
			</Modal>
			<Modal ref={purchaseInformationModalRef}>
				<CommonModal
					style={{ padding: 20 }}
					closeModal={closePurchaseInformationModal}
					content={
						<>
							<EventContextHeader listings={contextListings} />
							<PurchaseInformation inventoryItem={selectedRow ?? undefined} onSuccess={closePurchaseInformationModal} />
						</>
					}
					title={"Purchase Information"}
				/>
			</Modal>
			<Modal ref={tagModalRef}>
				{selectedRow?.Listing_ID && selectedRow?.Item_ID ? (
					<TagComponent
						itemId={[selectedRow.Listing_ID]}
						childIds={unsoldItemIds(eventInventoryAll.data, selectedRow)}
						type="inventory"
						modalHeader="Inventory tags"
						originalTags={selectedRow?.Tags?.split("|") ?? []}
						onComplete={closeTagModal}
						eventHeaderListings={contextListings}
					/>
				) : null}
			</Modal>
			<Modal ref={splitListingModalRef}>
				<CommonModal
					style={{ padding: 20 }}
					closeModal={closeSplitListingModal}
					content={
						<>
							<EventContextHeader listings={contextListings} />
							<SplitListing inventoryItem={selectedRow ?? undefined} onSuccess={closeSplitListingModal} />
						</>
					}
					title={"Split Listing"}
				/>
			</Modal>

			<Modal ref={relistModalRef} style={{ width: 400 }}>
				<CommonModal
					style={{ padding: 20 }}
					closeModal={closeRelistModal}
					title="Confirm Re-listing"
					content={
						<div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
							<div>Are you sure you want to re-list the listing?</div>
							<div style={{ fontSize: "1.4rem", color: "var(--textSecondary)" }}>
								Listing ID: {selectedRow?.Listing_ID}
							</div>
						</div>
					}
					buttons={
						<div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
							<Button
								style={{ width: 90 }}
								disabled={relistLoading}
								onClick={closeRelistModal}
								variant={ButtonVariant.Secondary}
								controlSize="sm">
								Cancel
							</Button>
							<Button
								style={{ width: 90 }}
								disabled={relistLoading}
								isLoading={relistLoading}
								onClick={handleRelistConfirm}
								variant={ButtonVariant.Primary}
								controlSize="sm">
								Yes
							</Button>
						</div>
					}
				/>
			</Modal>

			<Modal ref={eDeliveryModalRef} style={{ width: 475 }}>
				<CommonModal
					style={{ padding: 20 }}
					closeModal={closeEDeliveryModal}
					title={isEDeliveryOn ? "Confirm Remove E-Delivery" : "Confirm Add E-Delivery"}
					content={
						<div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
							<div>
								Are you sure you want to {isEDeliveryOn ? "remove" : "add"} E-Delivery
								{isEDeliveryOn ? " from" : " to"} the listing?
							</div>
							<div style={{ fontSize: "1.4rem", color: "var(--textSecondary)" }}>
								Listing ID: {selectedRow?.Listing_ID}
							</div>
						</div>
					}
					buttons={
						<div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
							<Button
								style={{ width: 90 }}
								disabled={eDeliveryLoading}
								onClick={closeEDeliveryModal}
								variant={ButtonVariant.Secondary}
								controlSize="sm">
								Cancel
							</Button>
							<Button
								style={{ width: 90 }}
								disabled={eDeliveryLoading}
								isLoading={eDeliveryLoading}
								onClick={handleEDeliveryConfirm}
								variant={ButtonVariant.Primary}
								controlSize="sm">
								Yes
							</Button>
						</div>
					}
				/>
			</Modal>

			<Modal ref={duplicateModalRef} style={{ width: 1250, maxWidth: "95vw" }}>
				<DuplicateListing
					listingIds={selectedRow?.Listing_ID ? [selectedRow.Listing_ID] : []}
					headerListings={contextListings}
					hasSoldListings={!!selectedRow?.Invoice_ID}
					closeModal={closeDuplicateModal}
					onSuccess={() => fetchInventory(true)}
				/>
			</Modal>

			<Modal ref={viewHistoryModalRef} style={{ width: 1150 }}>
				<CommonModal
					width={1150}
					style={{ padding: 0 }}
					closeModal={closeViewHistoryModal}
					title="View History"
					content={
						<ViewHistory
							key={selectedRow?.Listing_ID ?? "view-history"}
							itemIds={selectedRow?.Child_Item_IDs ?? (selectedRow?.Item_ID ? [selectedRow.Item_ID] : [])}
							listingDetails={{
								listingId: selectedRow?.Listing_ID,
								eventName: eventItem?.Event_Headliner ?? eventItem?.Event_Name,
								date: eventItem?.Event_Date ? formatDate(eventItem.Event_Date, DateFormatsEnum["MM/DD/YYYY"]) : undefined,
								time: eventItem?.Event_Time ?? undefined,
								venue: eventItem?.Venue_Name,
								section: selectedRow?.Section,
								row: selectedRow?.Row,
								seats: selectedRow?.Seats,
							}}
							onClose={closeViewHistoryModal}
						/>
					}
				/>
			</Modal>

			<Modal ref={changeSeatsModalRef} style={{ width: 950, maxWidth: "95vw" }}>
				<CommonModal
					style={{ padding: 0 }}
					closeModal={closeChangeSeatsModal}
					title="Change Seats"
					content={
						<ChangeSeats
							key={selectedRow?.Listing_ID ?? "change-seats"}
							listingIds={selectedRow?.Listing_ID ? [selectedRow.Listing_ID] : []}
							onClose={closeChangeSeatsModal}
							onSuccess={() => fetchInventory(true)}
						/>
					}
				/>
			</Modal>

			<div data-listing-selection-area className={helperStyles.displayContents}>
				<ContextMenu ref={contextRef}>
					<InventoryContextMenu
						selectedRow={selectedRow}
						closeContextMenu={closeContextMenu}
						onHelpOpen={openHelpAndSupportModal}
						onContactUserOpen={openContactUserModal}
						onCreateInvoiceOpen={openCreateInvoiceContextModal}
						onInternalInvoiceOpen={openInternalInvoiceModal}
						onSchedulePriceOpen={openSchedulePriceModal}
						onFlashSaleOpen={openFlashSaleModal}
						onPurchaseInfoOpen={openPurchaseInformationModal}
						onTagOpen={openTagModal}
						onSplitListingsOpen={openSplitListingModal}
						onExternalNotesModalOpen={openExternalNotesModal}
						onShareUnshareModalOpen={openShareUnshareModal}
						onInternalNotesModalOpen={openInternalNotesModal}
						onRelistOpen={openRelistModal}
						onEDeliveryOpen={openEDeliveryModal}
						onDuplicateOpen={openDuplicateModal}
						onViewHistoryOpen={openViewHistoryModal}
						onChangeSeatsOpen={openChangeSeatsModal}
					/>
				</ContextMenu>
			</div>
		</SectionContainer>
	);
};
